import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Table } from '@getsynapse/design-system';
import { UserDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import { SortingType } from 'utils/customTypes';
import { orderBy } from 'lodash';
import { USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS } from 'utils/constants/dashboard';
import UserDrillDownTableHeader from './UserDrillDownTableHeader/UserDrillDownTableHeader';
import UserDrillDownTableBody from './UserDrillDownTableBody/UserDrillDownTableBody';
import { useSelector } from 'react-redux';
import drilldownSelectors from 'state/normalized/insights/drilldownSelectors';
import DrillTableHeaderFilters from 'Pages/DashboardsDetailsPage/components/DrillTableHeaderFilters';
import { handleUserExportData } from 'Pages/DashboardsDetailsPage/components/DrillDownSidePanel/drillDownDataExportHandlers';
import { insightDrilldownMetadataSelectors } from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import TableSkeletonLoader from 'Organisms/TableSkeletonLoader/TableSkeletonLoader';

const columnMap = new Map<string, string>([
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_ID, 'user_number.value'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_TYPE, 'user_type'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_TITLE, 'title'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_NUMBER, 'projects'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.NAME, 'user_name'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TIME_OFF, 'timeoff_hours_in_range'],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DEFAULT_CAPACITY_HOURS,
    'default_capacity_hours_in_range',
  ],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CAPACITY_HOURS,
    'capacity_hours_in_range',
  ],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS,
    'allocated_hours_in_range',
  ],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS, 'actual_hours_in_range'],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.AVAILABLE_HOURS,
    'available_hours_in_range',
  ],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SCHEDULED_HOURS,
    'scheduled_hours_in_range',
  ],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.JOB_TITLE, 'job_title'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.JOB_FUNCTIONS, 'job_functions'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SKILLS, 'skills'],
  [
    USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LEARNING_TEAMS,
    'learning_teams[0].name',
  ],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE, 'user_type'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ROLE, 'user_role'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.EMAIL, 'email'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CREATED_AT, 'created_at'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LAST_LOGIN_DATE, 'last_login_date'],
  [USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DISABLED_AT, 'user_data.disabled_at'],
]);

const UserDrillDownTable = () => {
  const items = useSelector(drilldownSelectors.selectActiveDrilldownUsers);
  const cardName = useSelector(
    drilldownSelectors.selectActiveDrilldownCardName
  );
  const isLoading = useSelector(
    insightDrilldownMetadataSelectors.selectIsActiveDrilldownLoading
  );
  const exportItems = () => {
    handleUserExportData(items);
  };
  const [orderByParam, setOrderByParam] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const orderByUserTableData = (
    items: UserDrillDownItem[],
    column: string,
    order: SortingType
  ) => {
    if (!items || items.length === 0) return [];

    const fieldName = columnMap.get(column) || '';
    const finalData = orderBy(items, [fieldName], [order]);
    return finalData;
  };

  const sortedItems = useMemo(() => {
    return orderByUserTableData(items, orderByParam, order);
  }, [items, orderByParam, order]);

  const handleSort = useCallback(
    (column: string) => {
      const isSameColumn = column === orderByParam;
      const newOrder = isSameColumn && order === 'asc' ? 'desc' : 'asc';
      setOrderByParam(column);
      setOrder(newOrder);
    },
    [orderByParam, order]
  );

  return (
    <div>
      <DrillTableHeaderFilters
        title={cardName}
        onExportClick={exportItems}
        exportEnabled
      />
      <div
        className={classnames(
          'w-full rounded-b overflow-auto border border-neutral-lighter-two max-h-filtered-table-body'
        )}
      >
        {isLoading && <TableSkeletonLoader />}
        {!isLoading && (
          <Table
            className={classnames('relative', {
              'max-w-full border-0 w-full': items.length,
              'w-full': !items.length,
            })}
            canSelectRows={false}
            data={{
              headData: {
                stickyHeader: true,
                handleSort,
                headCells: [],
              },
              rows: [],
              total: items.length,
            }}
            data-cy='team-dashboards-table'
          >
            <UserDrillDownTableHeader handleSort={handleSort} />
            <UserDrillDownTableBody items={sortedItems} />
          </Table>
        )}
      </div>
    </div>
  );
};

export default UserDrillDownTable;
